import React, { useState, useMemo, useEffect, useRef } from 'react';
import tw from 'tailwind-styled-components';
import {
    FaShoppingBag,
    FaMapMarkerAlt,
    FaExclamationCircle,
    FaCheckCircle,
    FaTruck,
    FaEye,
    FaArrowLeft,
    FaClock,
    FaTimes,
    FaUndo,
    FaBan,
    FaReceipt,
    FaBoxOpen,
    FaHeadset,
    FaImage,
    FaTrash,
    FaTimesCircle,
} from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useOrders from '../hooks/useOrders';
import moment from 'moment';
import useReturns from '../hooks/useReturns';
import { OrdersLoading } from '../components/SkeletonLoading/OrdersLoading';

const MyOrders = () => {
    const navigate = useNavigate();
    const user = useSelector((state) => state.UserReducer.user);
    const { data: ordersData, isLoading, error, refetch } = useOrders();
    const { data: returnsData } = useReturns();

    // Memoize orders array for performance
    const orders = useMemo(() => {
        return Array.isArray(ordersData?.orders) ? ordersData.orders :
            Array.isArray(ordersData) ? ordersData : [];
    }, [ordersData]);

    const getOrderReturnData = (orderId) => {
        if (!returnsData || !Array.isArray(returnsData)) return null;
        return returnsData.find(r => String(r.order_id) === String(orderId));
    }

    // Memoize order status info for performance
    const getOrderStatusInfo = useMemo(() => (status) => {
        const statusMap = {
            'pending': { label: 'Pending', icon: FaClock, color: 'text-gray-600 bg-gray-100' },
            'confirmed': { label: 'Confirmed', icon: FaCheckCircle, color: 'text-green-600 bg-green-100' },
            'shipped': { label: 'Shipped', icon: FaTruck, color: 'text-yellow-700 bg-yellow-100' },
            'delivered': { label: 'Delivered', icon: FaCheckCircle, color: 'text-green-600 bg-green-100' },
            'cancelled': { label: 'Cancelled', icon: FaExclamationCircle, color: 'text-red-600 bg-red-100' },
            'return_pending': { label: 'Return Pending', icon: FaUndo, color: 'text-yellow-700 bg-yellow-100' },
            'approved': { label: 'Return Approved', icon: FaCheckCircle, color: 'text-green-600 bg-green-100' },
            'returned': { label: 'Returned', icon: FaBoxOpen, color: 'text-green-600 bg-green-100' },
            'refunded': { label: 'Refunded', icon: FaReceipt, color: 'text-green-600 bg-green-100' },
            'rejected': { label: 'Return Rejected', icon: FaBan, color: 'text-red-600 bg-red-100' },
        };
        return statusMap[status] || statusMap['pending'];
    }, []);

    const formatCurrency = useMemo(() => (amount) => {
        return `₹${parseFloat(amount).toFixed(2)}`;
    }, []);
// navigate to order details page
    const handleViewOrder = (order) => {
        navigate(`/order-details?orderId=${order.id}`);
    };

    const getProductImage = (item) => {
        const imageUrl = item.product?.images?.[0]?.image_url ||
            item.product?.image_url ||
            item.image_url ||
            null;
        return imageUrl;
    };

    // Reusable Header Component
    const OrdersHeader = () => (
        <Header>
            <div className="flex items-center space-x-3">
                <BackButton onClick={() => navigate(-1)}>
                    <FaArrowLeft className="w-5 h-5" />
                </BackButton>
                <div className="flex items-center space-x-2">
                    <FaShoppingBag className="w-5 h-5 text-kuality-blue" />
                    <PageTitle >My Orders</PageTitle>
                </div>
            </div>
            {orders.length > 0 && (
                <div className="flex justify-between items-center">
                    <div className="text-lg  font-semibold text-kuality-blue">
                        {orders.length} {orders.length === 1 ? 'order' : 'orders'}
                    </div>
                </div>
            )}
        </Header>
    );

    if (!user) {
        return (
            <PageContainer>
                <OrdersHeader />
                <div className="flex-1 flex flex-col items-center justify-center pb-12">
                    <FaExclamationCircle className="text-6xl text-gray-500 mb-4" />
                    <h2 className="text-xl font-semibold text-gray-500 mb-2">Please Login or Register</h2>
                    <p className="text-gray-500">You need to be logged in to view your orders.</p>
                </div>
            </PageContainer>
        );
    }

    if (isLoading) {
        return (
            <PageContainer>
                <OrdersHeader />
                <OrdersLoading />
            </PageContainer>
        );
    }

    if (error) {
        return (
            <PageContainer>
                <OrdersHeader />
                <ErrorContainer>
                    {/* <FaExclamationCircle className="text-4xl text-red-500 mb-4" /> */}
                    <ErrorTitle>Unable to Load Orders</ErrorTitle>
                    <ErrorMessage>Something went wrong while fetching your orders.</ErrorMessage>
                    <RetryButton onClick={() => refetch()}>
                        Try Again
                    </RetryButton>
                </ErrorContainer>
            </PageContainer>
        );
    }

    if (orders.length === 0) {
        return (
            <PageContainer>
                <OrdersHeader />
                <EmptyState>
                    <EmptyStateIcon className="text-6xl text-gray-500 mb-4" />
                    <EmptyStateTitle>No Orders Yet</EmptyStateTitle>
                    {/* <EmptyStateMessage>You haven't placed any orders yet. Start shopping to see your orders here!</EmptyStateMessage> */}
                    <StartShoppingButton onClick={() => navigate('/')}>
                        Start Shopping
                    </StartShoppingButton>
                </EmptyState>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <OrdersHeader />

            <OrdersList>
                {orders.map((order) => {
                    const returnInfo = getOrderReturnData(order.id);
                    let statusKey = order.status;
                    if (returnInfo) {
                        // if the return status is 'pending', map to 'return_pending' for status display
                        statusKey = returnInfo.status === 'pending' ? 'return_pending' : returnInfo.status;
                    }
                    const statusInfo = getOrderStatusInfo(statusKey);
                    const StatusIcon = statusInfo.icon;
                    const itemCount = order.items?.length || 0;
                    const firstItem = order.items?.[0];
                    const firstItemImage = firstItem ? getProductImage(firstItem) : null;

                    return (
                        <OrderCard key={order.id}>
                            <OrderHeader>
                                <OrderHeaderContent>
                                    <OrderIconAndInfo>
                                        <OrderImageContainer>
                                            {firstItemImage ? (
                                                <OrderCardImage
                                                    src={firstItemImage}
                                                    alt={firstItem.product_name || 'Product'}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <OrderImagePlaceholder
                                                style={{ display: firstItemImage ? 'none' : 'flex' }}
                                            >
                                                <FaShoppingBag className="text-kuality-blue" />
                                            </OrderImagePlaceholder>
                                        </OrderImageContainer>
                                        <OrderInfo>
                                            <OrderId>Order ID: {order.id}</OrderId>
                                            <OrderDate>
                                                {moment(order.created_at).format('MMM DD, YYYY')}
                                            </OrderDate>
                                        </OrderInfo>
                                    </OrderIconAndInfo>
                                    <StatusBadge className={statusInfo.color}>
                                        <StatusIcon className="text-sm" />
                                        <span className="text-sm font-medium">{statusInfo.label}</span>
                                    </StatusBadge>
                                </OrderHeaderContent>
                            </OrderHeader>

                            <OrderDetails>
                                <OrderDetailsContent>
                                    <OrderSummary>
                                        <ItemSummary>
                                            {itemCount} {itemCount === 1 ? 'item' : 'items'}
                                            {firstItem && ` • ${firstItem.product_name || `Product ${firstItem.product_id}`}`}
                                            {itemCount > 1 && ` and ${itemCount - 1} more`}
                                        </ItemSummary>
                                        <TotalAmount>
                                            {formatCurrency(order.total_amount)}
                                        </TotalAmount>
                                        {order.payment_method && (
                                            <PaymentMethod>
                                                Payment: {order.payment_method}
                                            </PaymentMethod>
                                        )}
                                    </OrderSummary>
                                    <OrderActions>
                                        <ActionButton onClick={() => handleViewOrder(order)}>
                                            <FaEye className="mr-2" />
                                            Details
                                        </ActionButton>
                                    </OrderActions>
                                </OrderDetailsContent>
                            </OrderDetails>
                        </OrderCard>
                    );
                })}
            </OrdersList>
        </PageContainer>
    );
};

// Styled Components 
const PageContainer = tw.div`
   min-h-screen w-full bg-white flex flex-col
`;

const Header = tw.div`
    flex items-center justify-between p-3 md:p-2 bg-white border-b border-gray-200 sticky top-0 z-10
`;

const PageTitle = tw.h1`
    text-lg font-semibold text-gray-900 tracking-wider md:text-xl
`;

const BackButton = tw.button`
     text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100 transition-colors
`;

const OrdersList = tw.div`
    space-y-4 md:px-16 md:py-8 p-5
`;

const OrderCard = tw.div`
    bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200
    hover:border-kuality-blue
`;

const OrderHeader = tw.div`
    p-4 border-b border-gray-100
`;

const OrderHeaderContent = tw.div`
    flex items-center justify-between
`;

const OrderIconAndInfo = tw.div`
    flex items-center space-x-3
`;

const OrderImageContainer = tw.div`
    relative w-12 h-12 rounded-lg overflow-hidden
`;

const OrderCardImage = tw.img`
    w-full h-full object-cover
`;

const OrderImagePlaceholder = tw.div`
    w-full h-full bg-gray-100 flex items-center justify-center 
`;

const OrderInfo = tw.div`
    flex flex-col
`;

const OrderId = tw.p`
    font-semibold text-gray-800
`;

const OrderDate = tw.p`
    text-sm text-gray-600
`;

const StatusBadge = tw.div`
    flex items-center space-x-2 px-3 py-1 rounded-full
`;

const OrderDetails = tw.div`
    p-4
`;

const OrderDetailsContent = tw.div`
    flex items-center justify-between
`;

const OrderSummary = tw.div`
    flex-1
`;

const ItemSummary = tw.p`
    text-sm text-gray-600 mb-1
`;

const TotalAmount = tw.p`
    font-semibold text-lg text-gray-700
`;

const PaymentMethod = tw.p`
    text-xs text-gray-500 mt-1
`;

const OrderActions = tw.div`
    flex space-x-2
`;

const ActionButton = tw.button`
    flex items-center px-3 py-2 text-kuality-blue text-sm font-medium rounded-lg 
    hover:bg-kuality-blue hover:text-white transition-colors border border-kuality-blue
    hover:border-kuality-blue
`;

const EmptyState = tw.div`
    flex-1 flex flex-col items-center justify-center text-center pb-12
`;

const EmptyStateIcon = tw(FaShoppingBag)``;

const EmptyStateTitle = tw.h3`
    text-xl font-semibold text-gray-600 mb-2
`;

const StartShoppingButton = tw.button`
    bg-kuality-blue text-white px-6 py-3 rounded-lg hover:bg-kuality-blue transition-colors
`;

const ErrorContainer = tw.div`
    flex-1 flex flex-col items-center justify-center text-center pb-12
`;

const ErrorTitle = tw.h3`
    text-lg font-semibold text-gray-800 mb-2
`;

const ErrorMessage = tw.p`
    text-gray-600 mb-4
`;

const RetryButton = tw.button`
    bg-kuality-blue text-white px-4 py-2 rounded-lg hover:bg-kuality-blue transition-colors
`;

export default MyOrders;